<script>
import StickerFavoritesAPI from 'dashboard/api/stickerFavorites';
import { toast } from 'dashboard/composables/useToast';

export default {
  name: 'StickerPanel',
  emits: ['send', 'close'],
  data() {
    return {
      isLoading: false,
      stickers: [],
      deleteMenuVisible: false,
      deleteMenuStyle: {},
      selectedSticker: null,
    };
  },
  mounted() {
    this.fetchStickers();
  },
  methods: {
    close() {
      this.$emit('close');
    },
    async fetchStickers() {
      this.isLoading = true;
      try {
        const { data } = await StickerFavoritesAPI.get();
        this.stickers = data;
      } catch {
        toast.error(
          this.$t('CONVERSATION.DINASTIA_ACTIONS.STICKER.UPLOAD_FAILED')
        );
      } finally {
        this.isLoading = false;
      }
    },
    async onFileSelected(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > 500 * 1024) {
        toast.error(this.$t('CONVERSATION.DINASTIA_ACTIONS.STICKER.TOO_LARGE'));
        return;
      }

      try {
        const { data } = await StickerFavoritesAPI.upload(
          file,
          file.name.replace(/\.\w+$/, '')
        );
        this.stickers.unshift(data);
        toast.success(
          this.$t('CONVERSATION.DINASTIA_ACTIONS.STICKER.UPLOADED')
        );
      } catch {
        toast.error(
          this.$t('CONVERSATION.DINASTIA_ACTIONS.STICKER.UPLOAD_FAILED')
        );
      }
      event.target.value = '';
    },
    sendSticker(sticker) {
      this.$emit('send', sticker);
    },
    showDeleteMenu(event, sticker) {
      this.selectedSticker = sticker;
      this.deleteMenuStyle = {
        top: `${event.clientY}px`,
        left: `${event.clientX}px`,
      };
      this.deleteMenuVisible = true;
      const closeMenu = () => {
        this.deleteMenuVisible = false;
        document.removeEventListener('click', closeMenu);
      };
      setTimeout(() => document.addEventListener('click', closeMenu), 0);
    },
    async deleteSticker() {
      if (!this.selectedSticker) return;
      try {
        await StickerFavoritesAPI.delete(this.selectedSticker.id);
        this.stickers = this.stickers.filter(
          s => s.id !== this.selectedSticker.id
        );
        toast.success(this.$t('CONVERSATION.DINASTIA_ACTIONS.STICKER.DELETED'));
      } catch {
        toast.error(
          this.$t('CONVERSATION.DINASTIA_ACTIONS.STICKER.DELETE_FAILED')
        );
      }
      this.deleteMenuVisible = false;
      this.selectedSticker = null;
    },
  },
};
</script>

<template>
  <div class="max-h-[420px] flex flex-col">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-n-weak dark:border-n-strong"
    >
      <h4 class="text-sm font-semibold text-n-slate-12 m-0">
        {{ $t('CONVERSATION.DINASTIA_ACTIONS.STICKER.LIBRARY_TITLE') }}
      </h4>
      <div class="flex gap-1">
        <woot-button
          variant="clear"
          size="tiny"
          icon="add"
          :title="$t('CONVERSATION.DINASTIA_ACTIONS.STICKER.UPLOAD')"
          @click="$refs.stickerUpload.click()"
        />
        <woot-button
          variant="clear"
          size="tiny"
          icon="dismiss"
          @click="close"
        />
      </div>
    </div>

    <!-- Hidden upload input -->
    <input
      ref="stickerUpload"
      type="file"
      accept="image/webp,image/png,image/gif"
      class="hidden"
      @change="onFileSelected"
    />

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <span class="spinner" />
    </div>

    <!-- Grid -->
    <div
      v-else-if="stickers.length > 0"
      class="grid grid-cols-4 gap-1.5 p-2.5 overflow-y-auto flex-1"
    >
      <div
        v-for="sticker in stickers"
        :key="sticker.id"
        class="relative aspect-square flex items-center justify-center cursor-pointer rounded-lg p-1 hover:bg-n-alpha-2 transition-colors"
        :title="
          sticker.name || $t('CONVERSATION.DINASTIA_ACTIONS.STICKER.SEND')
        "
        @click="sendSticker(sticker)"
        @contextmenu.prevent="showDeleteMenu($event, sticker)"
      >
        <img
          :src="sticker.thumb_url || sticker.url"
          :alt="sticker.name"
          loading="lazy"
          class="max-w-full max-h-full object-contain"
        />
        <span
          v-if="sticker.shared"
          class="absolute bottom-0.5 right-0.5 text-[9px] bg-n-blue-2 text-n-blue-11 px-1 rounded"
        >
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.STICKER.SHARED') }}
        </span>
      </div>
    </div>

    <!-- Empty -->
    <div
      v-else
      class="flex flex-col items-center justify-center py-12 px-6 text-center"
    >
      <p class="text-sm text-n-slate-11 mb-1">
        {{ $t('CONVERSATION.DINASTIA_ACTIONS.STICKER.EMPTY') }}
      </p>
      <p class="text-xs text-n-slate-9">
        {{ $t('CONVERSATION.DINASTIA_ACTIONS.STICKER.EMPTY_HINT') }}
      </p>
    </div>

    <!-- Delete context menu -->
    <Teleport to="body">
      <div
        v-if="deleteMenuVisible"
        class="fixed z-[999] bg-white dark:bg-n-solid-3 rounded-lg shadow-lg border border-n-weak dark:border-n-strong overflow-hidden"
        :style="deleteMenuStyle"
      >
        <button
          class="flex items-center gap-2 px-4 py-2.5 w-full text-left text-sm text-n-ruby-11 hover:bg-n-ruby-2 transition-colors"
          @click="deleteSticker"
        >
          <fluent-icon icon="delete" size="14" />
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.STICKER.DELETE') }}
        </button>
      </div>
    </Teleport>
  </div>
</template>
